import { I18n } from '@calendly/i18n'
import { IconButton } from '@calendly/ui/components/icon-button'
import { ArrowSyncIcon } from '@calendly/ui/components/icons'
import { Link } from '@calendly/ui/components/link'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { colorIconInfo } from '@calendly/ui/theme'
import { useMemo } from 'react'

import { selectById, useFetchCalendarAccountsQuery } from 'features/calendar_connections'
import { FormattedDateTime } from 'features/current_user'

const useCalendarInfo = (id) => {
  const { data, isSuccess } = useFetchCalendarAccountsQuery()

  return useMemo(() => {
    if (!isSuccess || !data) return {}

    const { calendars } = selectById(data, id)

    return calendars?.[0] || {}
  }, [isSuccess, data, id])
}

const LastSyncMessage = ({ lastSyncAt }) => {
  if (!lastSyncAt) return I18n.t('publisher.calendar.outlook_desktop.not_synced')

  return <FormattedDateTime value={lastSyncAt} />
}

const PluginInfo = ({ id }) => {
  const { installedPlugin } = useCalendarInfo(id)

  if (!installedPlugin) return null

  return (
    <>
      {`${I18n.t('publisher.calendar.outlook_desktop.version')}: ${installedPlugin.pluginVersion}`}
      {'. '}
      {I18n.t('publisher.calendar.device_information')}
      {': '}
      {installedPlugin.osVersion}
      {'. '}
      {I18n.t('publisher.calendar.last_synced')}
      {': '}
      <LastSyncMessage lastSyncAt={installedPlugin.lastSyncAt} />
    </>
  )
}

const UpdateButton = ({ id }) => {
  const isMobile = useDeviceType('isPhone')

  const { installedPlugin, latestPlugin } = useCalendarInfo(id)

  if (!installedPlugin || !latestPlugin) return null

  const canUpdate = latestPlugin.version !== installedPlugin.pluginVersion

  if (!canUpdate || !latestPlugin.downloadUrl) return null

  return isMobile ? (
    <IconButton
      onClick={() => window.location.assign(latestPlugin.downloadUrl)}
      title={I18n.t('publisher.calendar.outlook_desktop.update_available')}
    >
      <ArrowSyncIcon color={colorIconInfo} size="large" />
    </IconButton>
  ) : (
    <Link href={latestPlugin.downloadUrl}>
      {I18n.t('publisher.calendar.outlook_desktop.update_available')}
    </Link>
  )
}

export const OutlookDesktopInfo = ({ id, blocked }) => (
  <>
    <PluginInfo id={id} />
    {!blocked && <UpdateButton id={id} />}
  </>
)
